import * as React from "react"
import { cn } from "@/lib/utils"
import { ChevronIcon } from "../icons/Chevron"

export interface AccordionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * The question text displayed in the header
   */
  question: string
  /**
   * The answer text displayed when expanded
   */
  answer: string
  /**
   * Whether this accordion item is expanded (controlled)
   */
  expanded?: boolean
  /**
   * Callback when expand/collapse is toggled
   */
  onToggle?: () => void
}

/**
 * Accordion component for FAQ items
 *
 * Displays a question that can be expanded to reveal an answer.
 * Uses smooth grid-based animation for expand/collapse transitions.
 *
 * @example
 * ```tsx
 * // Controlled usage
 * <Accordion
 *   question="What is ANYW3AR?"
 *   answer="ANYW3AR is an NFC product linking platform..."
 *   expanded={isOpen}
 *   onToggle={() => setIsOpen(!isOpen)}
 * />
 * ```
 */
export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({
    className,
    question,
    answer,
    expanded = false,
    onToggle,
    ...props
  }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col",
          "border-b border-gray-100",
          className
        )}
        {...props}
      >
        {/* Header - Always visible */}
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            "flex items-center justify-between",
            "py-[0.5rem]",
            "w-full",
            "bg-transparent border-none",
            "cursor-pointer transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-1 rounded-[0.3125rem]"
          )}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${question}`}
        >
          {/* Question text */}
          <span
            className="font-montserrat text-[0.9375rem] font-medium leading-[1.25rem] text-gray-700 text-left flex-1"
            style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
          >
            {question}
          </span>

          {/* Chevron icon */}
          <ChevronIcon
            size={12}
            className={cn(
              "text-gray-700 transition-transform duration-200 ml-[0.625rem] shrink-0",
              expanded && "rotate-180"
            )}
          />
        </button>

        {/* Expandable content */}
        <div
          className={cn(
            "grid transition-all duration-200 ease-in-out",
            expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          )}
        >
          <div className="overflow-hidden">
            <div className="py-[0.5rem]">
              <p
                className="font-montserrat text-[0.9375rem] font-normal leading-[1.25rem] text-gray-700"
                style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
              >
                {answer}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

Accordion.displayName = "Accordion"
